package com.evoapps.evoshift.ui.finance

import com.evoapps.evoshift.util.usesDecimalComma
import com.evoapps.evoshift.domain.model.RosterPayroll
import com.evoapps.evoshift.domain.model.SystemContractType
import com.evoapps.evoshift.ui.reports.ExportFile
import kotlin.math.abs
import kotlin.math.roundToLong

/** The column titles and contract names of [HoursExport], in the app's language. */
data class HoursExportLabels(
    val language: String,
    val month: String,
    val employee: String,
    val contract: String,
    val plannedHours: String,
    val clockedHours: String,
    val paidHours: String,
    val gross: String,
    /** SH-78: the month's bonuses (already part of the gross), deductions and expense refunds. */
    val bonuses: String,
    val deductions: String,
    val refunds: String,
    /**
     * SH-57: overtime hours at 50% and 100%, Sunday and holiday hours without a day off, the pay
     * and supplements for them, and the minimum-wage top-up (the money is part of the gross).
     */
    val overtime50Hours: String,
    val overtime100Hours: String,
    val sundayHolidayHours: String,
    val supplements: String,
    val minimumTopUp: String,
    /** SH-57: sick leave days and sick pay with the ZUS benefit, annual leave days and holiday pay (part of the gross). */
    val sickDays: String,
    val sickPay: String,
    val vacationDays: String,
    val holidayPay: String,
    /** SH-46: the gross for the rostered hours, and the employer cost planned and actual. */
    val plannedGross: String,
    val plannedEmployerCost: String,
    val employerCost: String,
    val currency: String,
    val contractName: (SystemContractType) -> String
)

/**
 * The hours and estimated gross per person for one month, as a CSV for the accountant (SH-56):
 * the one thing "export to payroll" means in practice. EVoShift does not run the pay; the
 * accountant's own software does, from these hours.
 *
 * One row per person. Bonuses, deductions and expense refunds (SH-78) are three columns after the
 * gross rather than rows of their own, so every row stays one person's month and the columns add
 * up; the bonuses column is already part of the gross, deductions and refunds are not. SH-57:
 * then the overtime hours (50%, 100%), the Sunday and holiday hours without a day off, what they
 * add (pay for the hours over a salary's norm and the supplements) and the minimum-wage top-up,
 * all already part of the gross; the sick leave days, sick pay with the ZUS
 * benefit, annual leave days and holiday pay (part of the gross too). Then the
 * plan next to it (SH-46): the estimated gross for the rostered hours, and the employer cost
 * planned and actual (both with the refunds).
 *
 * Opens straight in Excel or Sheets in the app's language: Polish files use ";" and a decimal
 * comma (what Polish Excel expects), English ones "," and a decimal point. UTF-8 with a BOM so
 * Polish letters survive Excel, CRLF line ends (RFC 4180). Numbers carry no thousands separator.
 */
object HoursExport {
    const val MIME_TYPE = "text/csv"

    fun fileName(payroll: RosterPayroll): String =
        "evoshift-hours-${payroll.rosterId}.csv"

    fun file(payroll: RosterPayroll, labels: HoursExportLabels): ExportFile =
        ExportFile(fileName(payroll), MIME_TYPE, csv(payroll, labels))

    fun csv(payroll: RosterPayroll, labels: HoursExportLabels): String {
        val polish = usesDecimalComma(labels.language)
        val separator = if (polish) ";" else ","
        val decimal = if (polish) ',' else '.'
        val month = "${payroll.year}-${payroll.month.toString().padStart(2, '0')}"
        val header = listOf(
            labels.month, labels.employee, labels.contract, labels.plannedHours,
            labels.clockedHours, labels.paidHours, labels.gross,
            labels.bonuses, labels.deductions, labels.refunds,
            labels.overtime50Hours, labels.overtime100Hours, labels.sundayHolidayHours, labels.supplements, labels.minimumTopUp,
            labels.sickDays, labels.sickPay, labels.vacationDays, labels.holidayPay,
            labels.plannedGross, labels.plannedEmployerCost, labels.employerCost, labels.currency
        )
        val rows = payroll.employees.map { e ->
            listOf(
                month,
                e.employeeName,
                labels.contractName(e.contract),
                number(e.plannedHours, decimal),
                number(e.workedHours, decimal),
                number(e.paidHours, decimal),
                number(e.payslip.gross, decimal),
                number(e.bonuses, decimal),
                number(e.deductions, decimal),
                number(e.expenseRefunds, decimal),
                number(e.factors?.overtime50Hours ?: 0.0, decimal),
                number(e.factors?.overtime100Hours ?: 0.0, decimal),
                number(e.factors?.sundayHolidayHours ?: 0.0, decimal),
                number(e.factors?.let { it.overtimePay + it.supplements } ?: 0.0, decimal),
                number(e.factors?.minimumWageTopUp ?: 0.0, decimal),
                (e.factors?.sickDays ?: 0).toString(),
                number(e.factors?.let { it.sickPay + it.sickBenefit } ?: 0.0, decimal),
                (e.factors?.vacationDays ?: 0).toString(),
                number(e.factors?.let { it.holidayPay + it.holidayVariablePay } ?: 0.0, decimal),
                number(e.plannedGross, decimal),
                number(e.plannedEmployerCost, decimal),
                number(e.employerCost, decimal),
                payroll.currency
            )
        }
        return BOM + (listOf(header) + rows).joinToString("") { row ->
            row.joinToString(separator) { cell(it, separator) } + "\r\n"
        }
    }

    /**
     * Quotes a cell holding the separator, a quote or a line break, and defuses names that a
     * spreadsheet would run as a formula ("=", "+", "-", "@" first).
     */
    internal fun cell(value: String, separator: String): String {
        val safe = if (value.isNotEmpty() && value[0] in FORMULA_STARTS) "'$value" else value
        val needsQuotes = separator in safe || '"' in safe || '\n' in safe || '\r' in safe
        return if (needsQuotes) "\"" + safe.replace("\"", "\"\"") + "\"" else safe
    }

    /** Two decimals, rounded half up, no grouping: "1234.50" or "1234,50". */
    internal fun number(value: Double, decimal: Char): String {
        val cents = (abs(value) * 100).roundToLong()
        val sign = if (value < 0 && cents != 0L) "-" else ""
        return "$sign${cents / 100}$decimal${(cents % 100).toString().padStart(2, '0')}"
    }

    private const val BOM = "﻿"
    private val FORMULA_STARTS = setOf('=', '+', '-', '@')
}
